import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import { adminApi, publicAssetUrl } from '../../lib/adminApi';

const statusClass = {
  draft: 'bg-slate-100 text-slate-700',
  published: 'bg-emerald-100 text-emerald-700',
  archived: 'bg-amber-100 text-amber-700',
};

export default function AdminNewsList() {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');

  async function loadNews() {
    try {
      setLoading(true);
      setError('');
      const params = new URLSearchParams();
      if (status) params.set('status', status);
      if (q) params.set('q', q);
      const data = await adminApi(`/api/admin/news?${params.toString()}`);
      setNews(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadNews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  async function deleteNews(id) {
    if (!window.confirm('Delete this news permanently?')) return;
    try {
      await adminApi(`/api/admin/news/${id}`, { method: 'DELETE' });
      setNews((items) => items.filter((item) => item.id !== id));
    } catch (err) {
      alert(err.message);
    }
  }

  async function changeStatus(id, nextStatus) {
    try {
      await adminApi(`/api/admin/news/${id}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status: nextStatus }),
      });
      await loadNews();
    } catch (err) {
      alert(err.message);
    }
  }

  return (
    <AdminLayout
      title="News CMS"
      subtitle="Create, edit, publish, archive and delete institutional news securely."
      actions={
        <Link to="/adxm/news/create" className="rounded-xl bg-emerald-700 px-4 py-3 text-sm font-black text-white shadow hover:bg-emerald-800">
          + Create News
        </Link>
      }
    >
      <div className="mb-6 grid gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm md:grid-cols-[1fr_220px_auto]">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search title, summary or author..."
          className="rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-emerald-600"
        />
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-emerald-600"
        >
          <option value="">All status</option>
          <option value="draft">Draft</option>
          <option value="published">Published</option>
          <option value="archived">Archived</option>
        </select>
        <button onClick={loadNews} className="rounded-xl border border-emerald-200 px-5 py-3 text-sm font-black text-emerald-700 hover:bg-emerald-50">
          Search
        </button>
      </div>

      {error && <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-4 text-sm font-bold text-red-700">{error}</div>}
      {loading && <div className="rounded-xl bg-white p-6 text-sm font-bold text-slate-600 shadow-sm">Loading news...</div>}

      {!loading && (
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-slate-50 text-left text-xs font-black uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-5 py-4">News</th>
                  <th className="px-5 py-4">Status</th>
                  <th className="px-5 py-4">Author</th>
                  <th className="px-5 py-4">Date</th>
                  <th className="px-5 py-4 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {news.map((item) => (
                  <tr key={item.id} className="align-top hover:bg-slate-50">
                    <td className="px-5 py-4">
                      <div className="flex gap-4">
                        <div className="h-16 w-20 flex-none overflow-hidden rounded-xl bg-slate-100">
                          {item.image_url ? (
                            <img src={publicAssetUrl(item.image_url)} alt="" className="h-full w-full object-cover" />
                          ) : null}
                        </div>
                        <div>
                          <div className="font-black text-slate-950">{item.title}</div>
                          <div className="mt-1 line-clamp-2 max-w-xl text-xs text-slate-600">{item.summary}</div>
                          {item.is_featured ? <span className="mt-2 inline-block rounded-full bg-yellow-100 px-2 py-1 text-[11px] font-black text-yellow-700">Featured</span> : null}
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <span className={`rounded-full px-3 py-1 text-xs font-black ${statusClass[item.status] || statusClass.draft}`}>{item.status}</span>
                    </td>
                    <td className="px-5 py-4 text-slate-600">{item.author || 'FPB'}</td>
                    <td className="px-5 py-4 text-slate-600">{item.published_at || item.created_at || '-'}</td>
                    <td className="px-5 py-4">
                      <div className="flex flex-wrap justify-end gap-2">
                        <Link to={`/adxm/news/edit/${item.id}`} className="rounded-lg border border-slate-200 px-3 py-2 font-bold text-slate-700 hover:bg-slate-100">Edit</Link>
                        {item.status !== 'published' ? (
                          <button onClick={() => changeStatus(item.id, 'published')} className="rounded-lg border border-emerald-200 px-3 py-2 font-bold text-emerald-700 hover:bg-emerald-50">Publish</button>
                        ) : (
                          <button onClick={() => changeStatus(item.id, 'draft')} className="rounded-lg border border-amber-200 px-3 py-2 font-bold text-amber-700 hover:bg-amber-50">Unpublish</button>
                        )}
                        <button onClick={() => changeStatus(item.id, 'archived')} className="rounded-lg border border-slate-200 px-3 py-2 font-bold text-slate-700 hover:bg-slate-100">Archive</button>
                        <button onClick={() => deleteNews(item.id)} className="rounded-lg border border-red-200 px-3 py-2 font-bold text-red-700 hover:bg-red-50">Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!news.length && (
                  <tr>
                    <td colSpan="5" className="px-5 py-10 text-center text-sm font-bold text-slate-500">No news found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
